import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ShieldAlert } from "lucide-react"
import { useState } from "react"
import { api, ErrorApi } from "@/api/cliente"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { BadgeEstatus, type Estatus } from "./BadgeEstatus"

/**
 * Diálogo para mover el expediente de estatus.
 *
 * Ofrece **sólo los destinos que el servidor declara legales**, pedidos a
 * `/transiciones-posibles`. Eso no es una comprobación de seguridad —el servidor
 * revalida en `/avanzar`, y ahí está la garantía— sino la diferencia entre un menú
 * con dos opciones que funcionan y uno con once de las que nueve dan 409.
 *
 * **Sin actualización optimista.** El avance puede ser rechazado por la compuerta
 * documental del paso 12, y pintar un éxito que el servidor va a negar es el peor
 * caso posible: el tramitador cree que el expediente avanzó y sigue con lo
 * siguiente.
 */

interface Posibles {
  estatus_actual: Estatus
  destinos: Estatus[]
}

export function DialogoAvanzar({
  expedienteId,
  onListo,
}: {
  expedienteId: number
  onListo?: () => void
}) {
  const [destino, setDestino] = useState<Estatus | "">("")
  const [forzar, setForzar] = useState(false)
  const [motivo, setMotivo] = useState("")
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()

  const posibles = useQuery({
    queryKey: ["transiciones-posibles", expedienteId],
    queryFn: () => api.get<Posibles>(`/api/v1/expedientes/${expedienteId}/transiciones-posibles`),
  })

  const avanzar = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/expedientes/${expedienteId}/avanzar`, {
        estatus_hasta: destino,
        override: forzar,
        motivo: forzar ? motivo : null,
      }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["expediente", expedienteId] })
      clienteQuery.invalidateQueries({ queryKey: ["transiciones-posibles", expedienteId] })
      clienteQuery.invalidateQueries({ queryKey: ["expedientes"] })
      setDestino("")
      setForzar(false)
      setMotivo("")
      onListo?.()
    },
  })

  /**
   * Documentos que faltan, leídos del sobre de error del servidor.
   *
   * No se calculan aquí. El backend es el único que sabe qué exige cada etapa —
   * `etapa_documentos_requeridos` es una tabla que un administrador ajusta sin
   * desplegar— y duplicar esa regla en el navegador garantizaría que las dos
   * versiones se separen.
   */
  const faltantes =
    avanzar.error instanceof ErrorApi && avanzar.error.codigo === "DOCUMENTOS_FALTANTES"
      ? ((avanzar.error.detalles?.faltantes as string[] | undefined) ?? [])
      : []

  if (posibles.isPending) {
    return (
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando destinos…
      </p>
    )
  }

  if (posibles.isError) {
    return (
      <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
        No se pudieron cargar los destinos posibles
      </p>
    )
  }

  // Conjunto vacío: COBRADO es terminal. Se dice, en vez de mostrar un menú
  // vacío que parece un fallo de carga.
  if (posibles.data.destinos.length === 0) {
    return (
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        <BadgeEstatus estatus={posibles.data.estatus_actual} /> es el estatus final: este expediente
        ya no se mueve.
      </p>
    )
  }

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label htmlFor="destino" className="mb-1 block text-xs font-medium uppercase">
          Mover a
        </label>
        <select
          id="destino"
          value={destino}
          onChange={(e) => setDestino(e.target.value as Estatus | "")}
          className="h-9 rounded-[var(--radius-control)] border px-2 text-sm"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--surface)",
            color: "var(--fg)",
          }}
        >
          <option value="">Selecciona un destino</option>
          {posibles.data.destinos.map((d) => (
            <option key={d} value={d}>
              {d.replaceAll("_", " ")}
            </option>
          ))}
        </select>
      </div>

      <button
        type="button"
        disabled={!destino || avanzar.isPending}
        onClick={() => avanzar.mutate()}
        className="h-9 rounded-[var(--radius-control)] px-3 text-sm font-medium disabled:opacity-50"
        style={{ backgroundColor: "var(--primary)", color: "var(--primary-fg)" }}
      >
        {avanzar.isPending ? "Moviendo…" : "Avanzar"}
      </button>

      {avanzar.isError && faltantes.length === 0 && (
        <p role="alert" className="w-full text-sm" style={{ color: "var(--destructive)" }}>
          {avanzar.error instanceof ErrorApi
            ? avanzar.error.message
            : "No se pudo avanzar el expediente"}
        </p>
      )}

      {faltantes.length > 0 && (
        <div
          role="alert"
          className="w-full superficie p-4"
          style={{ borderColor: "var(--warning)" }}
        >
          <p className="flex items-center gap-2 font-medium" style={{ color: "var(--warning)" }}>
            <ShieldAlert size={18} aria-hidden="true" />
            Faltan documentos para salir de esta etapa
          </p>
          {/* Se lista QUÉ falta. Un «no se puede avanzar» a secas obliga a
              adivinar, y el tramitador acaba pidiéndole al cliente todo otra vez. */}
          <ul className="mt-2 ml-6 list-disc text-sm">
            {faltantes.map((f) => (
              <li key={f}>{f.replaceAll("_", " ")}</li>
            ))}
          </ul>

          {tienePermiso(usuario, "expediente:override_documental") && (
            <div className="mt-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={forzar}
                  onChange={(e) => setForzar(e.target.checked)}
                />
                Forzar el avance de todos modos
              </label>

              {forzar && (
                <div className="mt-2">
                  <label htmlFor="motivo" className="mb-1 block text-xs font-medium uppercase">
                    Motivo (obligatorio, mínimo 10 caracteres)
                  </label>
                  {/* El motivo queda en la bitácora, junto a la transición
                      marcada como forzada. Es lo que hace auditable el salto. */}
                  <textarea
                    id="motivo"
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    rows={2}
                    className="w-full rounded-[var(--radius-control)] border p-2 text-sm"
                    style={{
                      borderColor: "var(--border)",
                      backgroundColor: "var(--background)",
                      color: "var(--fg)",
                    }}
                  />
                  <button
                    type="button"
                    disabled={motivo.trim().length < 10 || avanzar.isPending}
                    onClick={() => avanzar.mutate()}
                    className="mt-2 h-9 rounded-[var(--radius-control)] px-3 text-sm font-medium disabled:opacity-50"
                    style={{ backgroundColor: "var(--warning)", color: "var(--background)" }}
                  >
                    Forzar avance
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
